<script>
import { deepClone } from '@lsfe/tools';
import { isEqual } from '../../utils/tools';
import { getValue, findNodeInTree } from './utils';

export default {
    props: {
        // 接受的值
        value: {
            required: true
        },
        // 初始列表数据
        data: {
            type: Array,
            required: true
        },
        disabled: [Boolean],
        // 筛选数据的关键字
        keyword: [String]
    },
    inject: ['treeSelect'],
    data() {
        return {
            treeData: [], // 实际展示的树形数据，筛选后的
            initialTreeData: [], // 通过data计算出来的树形数据
            tagData: null,
            defaultCheckKeys: [] // 用于在外部数据变化时改变数据
        };
    },
    computed: {
        innerValue() {
            return getValue(this.tagData, this.treeSelect.onlyLeaf, this.treeSelect.mergeValue, this.treeSelect.multiple, this.treeSelect.innerNodeConfig);
        }
    },
    watch: {
        keyword(value) {
            if (this.treeSelect.filterMethod) {
                return this.treeSelect.filterMethod(value);
            }
            this.$refs.tree.filter(value);
        },
        value(val) {
            this.handleValueChange(val);
        },
        data: {
            deep: true,
            handler() {
                this.genTreeData();
            }
        },
        disabled(val) {
            this.handleChangeDisabled(this.treeData, val);
        }
    },
    created() {
        this.genTreeData(false);
    },
    mounted() {
        if (this.value) {
            this.handleValueChange(this.value);
        }
    },
    methods: {
        // 处理传入的disabled变化, hasSelfDisabled标识是否有selfDisabled
        handleChangeDisabled(data, val, hasSelfDisabled = true) {
            data.forEach(item => {
                if (!hasSelfDisabled) {
                    item.selfDisabled = item.disabled;
                }
                if (!item.selfDisabled) {
                    item.disabled = val;
                }
                if (item.children) {
                    this.handleChangeDisabled(item.children, val, hasSelfDisabled);
                }
            });
        },
        genTreeData(needHandleValue = true) {
            const data = deepClone(this.data);
            if (this.treeSelect.needGenTree) {
                const result = [];
                data.forEach(item => {
                    // 查找父节点
                    const parent = data.find(item2 => item2.id === item.parentId);
                    // 记录节点自身的disabled，用于切换全局disabled时的状态
                    item.selfDisabled = item.disabled || (parent && parent.selfDisabled);
                    item.disabled = item.selfDisabled || this.disabled;
                    if (parent && parent.children) {
                        parent.children.push(item);
                    } else if (parent) {
                        parent.children = [item];
                    } else {
                        result.push(item);
                    }
                });
                this.initialTreeData = result;
            } else {
                // 需要处理添加disabled属性
                this.handleChangeDisabled(this.data, this.disabled, false);
                this.initialTreeData = this.data;
            }
            this.treeData = deepClone(this.initialTreeData);
            // 如果重新渲染了数据，需要重新渲染选中项
            if (this.value && needHandleValue) {
                this.handleValueChange(this.value, false);
            }
        },
        // 点击选择
        handleNodeClick(item) {
            if (this.treeSelect.checkbox || item.disabled || this.disabled || (this.treeSelect.editableMethod && !this.treeSelect.editableMethod(item))) {
                return;
            }
            if (this.treeSelect.multiple) {
                // 多选
                return this.formatValue(1, item);
            }
            if (!this.treeSelect.onlyLeaf || !item.children) {
                // 单选
                if (item[this.treeSelect.innerNodeConfig.value] !== this.innerValue) {
                    this.formatValue(0, item);
                }
            }
        },
        async selectAll() {
            this.$refs.tree.setCheckedNodes(this.treeData);
            await this.$nextTick(() => {
                this.tagData = this.$refs.tree.getCheckedNodes();
            });
            this.emitEvent();
        },
        // checkbox选择
        handleCheck(obj, item) {
            const { checkedNodes } = item;
            this.formatValue(2, checkedNodes);
        },
        handleDeleteOne(item, flag = false) {
            if (!flag || !item.children) {
                // 如果是普通删除，直接删除该点 (非checkbox选择的节点删除和叶子节点删除)
                this.tagData = this.tagData.filter(item2 => item2[this.treeSelect.innerNodeConfig.value] !== item[this.treeSelect.innerNodeConfig.value]);
            } else {
                // 需要删除该点下面的所有叶子节点（checkbox选择的叶子节点删除）
                const info = { nodeCount: 0, leafCount: 0, firstLeaf: undefined };
                this.treeEach(item, info);
                // 因为父子节点是按照顺序相连的，所以可以根据子节点的个数使用splice删除
                const firstNodeIndex = this.tagData.findIndex(v => v.id === item.id);
                this.tagData.splice(firstNodeIndex, info.nodeCount);
            }
            if (this.treeSelect.checkbox) {
                this.$refs.tree.setChecked(item[this.treeSelect.innerNodeConfig.value], false, true);
            }
            this.emitEvent();
        },
        // needEmit控制是否对用户抛出事件
        async handleValueChange(val, needEmit = true) {
            // 对value的监听是用来处理外部修改的正常回显
            if (!isEqual(this.innerValue, val) || !needEmit) {
                if (this.treeSelect.multiple) {
                    this.tagData = [];
                    (val || []).forEach(item => {
                        let nodeData = findNodeInTree(item, this.initialTreeData, this.treeSelect.innerNodeConfig.value);
                        nodeData = nodeData || {
                            [this.treeSelect.innerNodeConfig.value]: item,
                            [this.treeSelect.innerNodeConfig.label]: item,
                            beUndefined: true
                        };
                        this.tagData.push(nodeData);
                    });
                    if (this.treeSelect.checkbox) {
                        // checkbox选择
                        // 要转换成叶子节点再选中，因为非叶子结点在节点disabled的状态下无法触发check，会导致选择遗漏
                        const leafvalue = [];
                        this.nodeToLeaf(this.tagData.filter(item => !item.beUndefined), leafvalue);
                        await this.$refs.tree.setCheckedKeys(leafvalue);
                        await this.$nextTick(() => {
                            this.tagData = this.$refs.tree.getCheckedNodes().concat(this.tagData.filter(item => item.beUndefined));
                        });
                    }
                } else {
                    // 单选
                    const items = this.data.find(item => item[this.treeSelect.innerNodeConfig.value] === val);
                    this.tagData = items || {
                        [this.treeSelect.innerNodeConfig.value]: val,
                        [this.treeSelect.innerNodeConfig.label]: val,
                        beUndefined: true
                    };
                }
                this.$nextTick(() => {
                    this.emitEvent(needEmit, true);
                });
            }
        },
        // 把选中项全部转换为叶子节点
        nodeToLeaf(nodeDatas, result = []) {
            nodeDatas.forEach(item => {
                if (!item.children) {
                    return result.push(item[this.treeSelect.innerNodeConfig.value]);
                }
                this.nodeToLeaf(item.children, result);
            });
        },
        clearValue() {
            if (this.treeSelect.multiple) {
                this.tagData = [];
                if (this.treeSelect.checkbox) {
                    this.$refs.tree.setCheckedKeys([]);
                }
            } else {
                this.tagData = {};
            }
            this.emitEvent();
        },
        emitEvent(needEmit = true, isFromeValueChange) {
            this.$nextTick(() => {
                this.$emit('change', this.tagData, needEmit, isFromeValueChange);
            });
        },
        // 遍历树形数据，对数据进行删除
        treeEach(node, info) {
            // 计算有多少个节点
            info.nodeCount += 1;
            if (node.children) {
                node.children.forEach(item => this.treeEach(item, info));
            } else {
                // 计算有多少个叶子节点
                info.leafCount += 1;
                if (!info.firstLeaf) info.firstLeaf = node;
            }
        },
        formatValue(type, data) {
            // type: 0-单选 1-任意多选 2-CheckBox多选
            if (type === 0) {
                this.tagData = data;
                // 单选选中时，关闭下拉框
                if (this.treeSelect.closeAfterSelect) {
                    this.treeSelect.closePopover();
                }
            } else if (type === 1) {
                this.tagData = this.tagData || [];
                if (!this.innerValue) {
                    this.tagData = [data];
                } else {
                    const index = this.tagData.findIndex(item => item[this.treeSelect.innerNodeConfig.value] === data[this.treeSelect.innerNodeConfig.value]);
                    if (index === -1) {
                        // 点击的值没有被选中，添加
                        this.tagData.push(data);
                    } else {
                        // 点击的值已经选中，删除
                        this.tagData.splice(index, 1);
                    }
                }
            } else if (type === 2) {
                this.tagData = this.tagData || [];
                this.tagData = data.concat(this.tagData.filter(item => item.beUndefined));
            }
            this.emitEvent();
        },
        filterNode(value, data) {
            if (!value) return true;
            return `${data[this.treeSelect.innerNodeConfig.label]}`.includes(value) || `${data[this.treeSelect.innerNodeConfig.value]}`.includes(value);
        }
    },
    render() {
        const setSelected = nodeData => {
            if (this.treeSelect.multiple) {
                return (this.tagData || []).findIndex(item => item[this.treeSelect.innerNodeConfig.value] === nodeData[this.treeSelect.innerNodeConfig.value]) !== -1;
            }
            return this.tagData && this.tagData[this.treeSelect.innerNodeConfig.value] === nodeData[this.treeSelect.innerNodeConfig.value];
        };
        const renderContent = (h, { node, data }) => {
            return (
                <div class={`node-content ${data.disabled || this.disabled ? 'node-disabled' : ''} ${setSelected(data) ? 'node-selected' : ''}`}>
                    {this.treeSelect.renderNode ? this.treeSelect.renderNode(h, { node, data }) : <span>{data[this.treeSelect.innerNodeConfig.label]}</span>}
                </div>
            );
        };
        return (
            <div class='xx-tree-wrapper__inner'>
                <el-tree
                    ref='tree'
                    {...{ attrs: this.$attrs }}
                    data={this.treeData}
                    props={this.treeSelect.innerNodeConfig}
                    nodeKey={this.treeSelect.innerNodeConfig.value}
                    showCheckbox={this.treeSelect.checkbox}
                    filterNodeMethod={this.treeSelect.filterNodeMethod || this.filterNode}
                    autoExpandParent={false}
                    default-checked-keys={this.defaultCheckKeys}
                    disabled={this.disabled}
                    renderContent={renderContent}
                    on-node-click={this.handleNodeClick}
                    onCheck={this.handleCheck}></el-tree>
            </div>
        );
    }
};
</script>
